// src/components/IjrrGear/IjrrGear.js

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './IjrrGear.css';
import IjrrGearSidebar from './IjrrGearSidebar';
import IjrrGearProductGrid from './IjrrGearProductGrid';

const API_BASE_ENDPOINT = `${process.env.REACT_APP_API_BASE_URL}`;

// UPDATED: Changed default category from 'BestSeller' to 'Jolly Roger  Gear'
// Note: Using double space to match the DB record shown in your JSON output.
const INITIAL_CATEGORY = 'Jolly Roger  Gear'; 
const PER_PAGE = 12; 

export default function IjrrGear() {

    // Global state for products, categories, and loading status
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // State for filtering
    const [filters, setFilters] = useState({
        categoryName: INITIAL_CATEGORY,
        gender: [],
    });

    // State for pagination (perPage removed)
    const [pagination, setPagination] = useState({
        currentPage: 1,
        hasMore: false,
    });

    // --- API Fetching Functions ---

    // 1. Fetch Categories for the Sidebar
    const fetchCategories = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_ENDPOINT}/public-inventory/categories`);
            setCategories(response.data.categories || []);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    }, []);

    // 2. Fetch Paginated Products
    const fetchProducts = useCallback(async (newFilters, page = 1, append = false) => {
        let isCurrentlyLoading = false;
        setLoading(prev => {
            if (prev) {
                isCurrentlyLoading = true;
                return prev;
            }
            return true;
        });

        if (isCurrentlyLoading) return;

        const params = new URLSearchParams({
            page: page,
            per_page: PER_PAGE,
        });

        if (newFilters.categoryName) {
            params.append('categoryName', newFilters.categoryName);
        }
        if (newFilters.gender.length > 0) {
            params.append('gender', newFilters.gender.join(','));
        }

        try {
            const response = await axios.get(`${API_BASE_ENDPOINT}/public-inventory?${params.toString()}`);

            const newProducts = response.data.products || [];
            const newPagination = response.data.pagination || {};

            setProducts(prevProducts =>
                append ? [...prevProducts, ...newProducts] : newProducts
            );

            setPagination(prev => ({
                ...prev,
                currentPage: newPagination.current_page || 1,
                hasMore: newPagination.hasMore || false,
            }));

        } catch (error) {
            console.error("Error fetching products:", error);
            setProducts(prevProducts => (append ? prevProducts : []));
            setPagination(prev => ({ ...prev, hasMore: false }));

        } finally {
            setLoading(false);
        }
    }, []); 

    // --- Handlers ---

    // Handler for category/gender changes from the sidebar
    const handleFilterChange = useCallback((key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));
    }, []);

    // Handler for the "Load More" button
    const handleLoadMore = useCallback(() => {
        if (!loading && pagination.hasMore) {
            fetchProducts(filters, pagination.currentPage + 1, true);
        }
    }, [loading, pagination.hasMore, filters, pagination.currentPage, fetchProducts]);

    const handleToggleSidebar = () => {
        setIsSidebarOpen(prev => !prev);
    };


    // --- useEffect Hooks ---

    // 1. Initial Load: Fetch categories once
    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // 2. Filter Change Watcher: Fetch products whenever filters change
    useEffect(() => {
        fetchProducts(filters, 1, false);
    }, [filters, fetchProducts]);

    // --- Render ---

    return (
        <section className="space-py-80 ijrr-gear-page">
            <div className="custom-container">
                <div className="row">
                    <div className="col-12">
                        <div className="row position-relative">
                            {/* Sidebar */}

                            <IjrrGearSidebar
                                categories={categories}
                                filters={filters}
                                onFilterChange={handleFilterChange}
                                isSidebarOpen={isSidebarOpen}
                                setIsSidebarOpen={setIsSidebarOpen}
                                isLoading={loading && products.length === 0}
                            />


                            {/* Product Grid */}

                            <IjrrGearProductGrid
                                products={products}
                                onToggleSidebar={handleToggleSidebar}
                            />

                            {/* Loading Indicator / Load More Button */}
                            <div className="col-12 text-center my-4">
                                {/* Initial Load/Filter Loading Spinner */}
                                {loading && products.length === 0 && (
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                )}

                                {products.length > 0 && loading && pagination.currentPage > 0 && (
                                    <p>Loading more...</p>
                                )}

                                {products.length > 0 && pagination.hasMore && !loading && (
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleLoadMore}
                                    >
                                        Load More
                                    </button>
                                )}

                                {products.length > 0 && !pagination.hasMore && !loading && (
                                    <p className="text-muted">You've reached the end of the current selection.</p>
                                )}

                                {products.length === 0 && !loading && (
                                    <p>No products found matching the current filters.</p>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}